import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  ButtonBase,
  Grid2,
  Box,
  FormControlLabel,
  Switch,
  Link,
} from "@mui/material";
import Tour from "../TourInterface.ts";
import { colorMapper, getContrastTextColor } from "../colorUtils.ts";
import TourOverlay from "./TourOverlay.tsx";
import AxiosInstance from "../../../utils/Axios.ts";

interface TourCardProps {
  tour: Tour;
  refresh: () => void;
}

const TourCard: React.FC<TourCardProps> = ({ tour, refresh }) => {
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [supplierCalled, setSupplierCalled] = useState<boolean>(!!tour.supplier_called);

  useEffect(() => {
    setSupplierCalled(!!tour.supplier_called);
  }, [tour.supplier_called]);

  const handleCardClick = () => {
    setIsOverlayOpen(true);
  };

  const handleOverlayClose = () => {
    setIsOverlayOpen(false);
    refresh();
  };

  const backgroundColor = colorMapper(tour.color_choice);
  const textColor = getContrastTextColor(backgroundColor);

  const handleSupplierCalledChange = async (checked: boolean) => {
    const previousValue = supplierCalled;
    setSupplierCalled(checked);
    try {
      await AxiosInstance.patch(`/tour/patch/${tour.id}/`, {
        supplier_called: checked,
      });
      refresh();
    } catch (error) {
      setSupplierCalled(previousValue);
      console.error("Failed to update supplier_called", error);
    }
  };

  return (
    <ButtonBase
        onClick={handleCardClick}
        sx={{ display: "block", textAlign: "inherit", width: "100%", height: "100%" }}
      >
    <Card sx={{
          height: "100%",
          width: "100%",
          marginBottom: 1,
          boxShadow: 1,
          backgroundColor,
          color: textColor,
        }}>
      <CardContent>
        {/* Top Row: Name and Tour Code */}
        <Grid2 container spacing={2} alignItems="center">
          <Grid2 size={6}>
            <Typography variant="subtitle2" color={textColor} fontSize={"12px"} noWrap>
              <strong>{tour.name}</strong> - {tour.tour_code}
            </Typography>
          </Grid2>
          <Grid2 size={6}>
            <Typography variant="caption" color={textColor} textAlign={"right"} fontSize={"11px"} noWrap>
              <strong>Time:</strong> {tour.time_from} - {tour.time_to}
            </Typography>
            </Grid2>
        </Grid2>

        {/* Compact Details */}
        <Box>
          <Grid2  >
            <Grid2 container spacing={2} alignItems="center">
              <Grid2 size={3}>
              <Typography variant="body2" color={textColor} fontSize={"12px"} textAlign={"center"} noWrap>
                <strong>Sold:</strong> {tour.size}
              </Typography>
              </Grid2>
              <Grid2 size={3}>
              <Typography variant="body2" color={textColor} fontSize={"12px"} textAlign={"center"} noWrap>
                <strong>Final:</strong> {tour.final_pax}
              </Typography>
              </Grid2>
              <Grid2 size={6}>
                <Typography variant="body2" color={textColor} fontSize={"12px"} textAlign={"center"} noWrap>
                  <strong>Check-in:</strong> {tour.check_in_time}
                </Typography>
              </Grid2>
            </Grid2>
            <Grid2 container spacing={2} alignItems="center">
              <Grid2 size={3}>
              <Typography variant="body2" color={textColor} fontSize={"11px"} textAlign={"center"} noWrap>
                <strong>Park:</strong> {tour.park_spot}
              </Typography>
              </Grid2>
              <Grid2 size={3}>
              <Typography variant="body2" color={textColor} fontSize={"11px"} textAlign={"center"} noWrap>
                <strong>Sign:</strong> {tour.bus_sign}
              </Typography>
              </Grid2>
              <Grid2 size={4}>
              <Typography variant="body2" color={textColor} fontSize={"11px"} textAlign={"center"} noWrap>
                <strong>Bus:</strong> {tour.bus_name}
              </Typography>
              </Grid2>
            </Grid2>
          </Grid2>
        </Box>

        {/* Optional Comment */}
        {tour.comment && (
          <Box>
            <Typography variant="body2" color={textColor} fontSize={"11px"} textAlign={"center"} sx={{textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden", }}>
              <strong>Comment:</strong> {tour.comment}
            </Typography>
          </Box>
        )}
        {tour.prime_portal_link ? (
          <Box
            sx={{ mt: 0.5, display: "flex", justifyContent: "center" }}
            onClick={(event) => event.stopPropagation()}
          >
            <Link
              href={tour.prime_portal_link}
              target="_blank"
              rel="noopener noreferrer"
              fontSize="11px"
              color={textColor}
            >
              Open TripDesk
            </Link>
          </Box>
        ) : null}
        <Box
          sx={{ mt: 0.5, display: "flex", justifyContent: "center" }}
          onClick={(event) => event.stopPropagation()}
        >
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={supplierCalled}
                onChange={(event) => {
                  event.stopPropagation();
                  handleSupplierCalledChange(event.target.checked);
                }}
              />
            }
            label={<Typography fontSize={"11px"} color={textColor}>Supplier Called</Typography>}
          />
        </Box>
      </CardContent>
      {isOverlayOpen && <TourOverlay tour={tour} onClose={handleOverlayClose} />}
    </Card>
    </ButtonBase>
  );
};

export default TourCard;
